import NiceModal from '@ebay/nice-modal-react';
import dayjs from 'dayjs';
import { Autoplay } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import Button from 'components/field/Button';
import Image from 'components/global/Image';
import ManageWidgetsModal from 'components/global/ManageWidgets/ManageWidgetsModal';
import PageHeader from 'components/global/PageHeader';
import SwiperFooter from 'components/global/SwiperFooter';
import Typography from 'components/global/Typography';
import WidgetPageContainer from 'components/global/WidgetPageContainer';
import WidgetWrapper from 'components/global/WidgetWrapper';

import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useLocale from 'hooks/useLocale';

import { DCPerformanceList, DeliveryCompDistribution } from 'pages/Analysis/DeliveryCompanyTab/DeliveryCompany';
import { DriverPerformance, NumberOfOrdersOtoFlex, OrderValuesSummary } from 'pages/Analysis/OTOFlexTab/OTOFlex';
import { TotalPendingByDC } from 'pages/Analysis/Overview/Overview';
import OverviewCharts, { DeliveryAging, PickupAging } from 'pages/Analysis/Overview/OverviewCharts';
import PendingShipments from 'pages/Analysis/Overview/PendingShipments';
import {
	DeliverySLAPerformance,
	PickupSLAPerformance,
	PreparationSLAPerformance,
	SLAOverview
} from 'pages/Analysis/SLAPerformanceTab/SLAPerformance';
import {
	AvgBasketSize,
	OrderValuesSummarySales,
	OrdersByCity,
	ShipmentPaymentType
} from 'pages/Analysis/SalesTab/Sales';
import SalesBodyProvider from 'pages/Analysis/SalesTab/SalesBodyProvider';
import {
	CODCollected,
	DCShippingFees,
	OTOFees,
	ShippingFees,
	Summary
} from 'pages/Analysis/ShippingAccountingTab/ShippingAccounting';
import { chartsConfig } from 'pages/Analysis/analysisWidgetsConfig';
import {
	ClosedOrders,
	CurrentOrders,
	NumberOfOrders,
	OrderDistribution,
	Overview,
	ProcessingOrders
} from 'pages/Dashboard/Dashboard';

import { MONITOR_VIEW } from 'services/consts';

import AppLogger from 'utils/AppLogger';

import { faExpand, faEyeSlash } from '@fortawesome/pro-regular-svg-icons';
import { faObjectsColumn } from '@fortawesome/pro-solid-svg-icons';

import * as S from './DashboardMonitor.styled';
import { monitorViewRaw } from './monitorView';

export const widgetComponentMapping = {
	numberOfOrders: ({ widgetName, onApplyFilters, defaultFilters }) => (
		<NumberOfOrders widgetName={widgetName} onApplyFilters={onApplyFilters} defaultFilters={defaultFilters} />
	),
	overview: ({ widgetName, onApplyFilters, defaultFilters }) => (
		<Overview widgetName={widgetName} onApplyFilters={onApplyFilters} defaultFilters={defaultFilters} />
	),
	currentOrders: ({ widgetName, onApplyFilters, defaultFilters }) => (
		<CurrentOrders widgetName={widgetName} onApplyFilters={onApplyFilters} defaultFilters={defaultFilters} />
	),
	closedOrders: ({ widgetName, onApplyFilters, defaultFilters }) => (
		<ClosedOrders widgetName={widgetName} onApplyFilters={onApplyFilters} defaultFilters={defaultFilters} />
	),
	processingOrders: ({ widgetName, onApplyFilters, defaultFilters }) => (
		<ProcessingOrders widgetName={widgetName} onApplyFilters={onApplyFilters} defaultFilters={defaultFilters} />
	),
	'dashboard.orderDistribution': ({ widgetName }) => <OrderDistribution widgetName={widgetName} />,
	'analysis.overview': ({ widgetName, onApplyFilters, defaultFilters }) => (
		<OverviewCharts widgetName={widgetName} onApplyFilters={onApplyFilters} defaultFilters={defaultFilters} />
	),
	'analysis.pickupAging': ({ widgetName, onApplyFilters, defaultFilters }) => (
		<PickupAging widgetName={widgetName} onApplyFilters={onApplyFilters} defaultFilters={defaultFilters} />
	),
	'analysis.deliveryAging': ({ widgetName, onApplyFilters, defaultFilters }) => (
		<DeliveryAging widgetName={widgetName} onApplyFilters={onApplyFilters} defaultFilters={defaultFilters} />
	),
	'analysis.totalPendingByDC': ({ widgetName, onApplyFilters, defaultFilters }) => (
		<TotalPendingByDC widgetName={widgetName} onApplyFilters={onApplyFilters} defaultFilters={defaultFilters} />
	),
	'analysis.pendingShipments': ({ widgetName, onApplyFilters, defaultFilters }) => (
		<PendingShipments widgetName={widgetName} onApplyFilters={onApplyFilters} defaultFilters={defaultFilters} />
	),
	'analysis.sla.delay': ({ widgetName, onApplyFilters, defaultFilters }) => (
		<SLAOverview widgetName={widgetName} onApplyFilters={onApplyFilters} defaultFilters={defaultFilters} />
	),
	preparationSLAPerformance: ({ widgetName, onApplyFilters, defaultFilters }) => (
		<PreparationSLAPerformance
			widgetName={widgetName}
			onApplyFilters={onApplyFilters}
			defaultFilters={defaultFilters}
		/>
	),
	pickupSLAPerformance: ({ widgetName, onApplyFilters, defaultFilters }) => (
		<PickupSLAPerformance widgetName={widgetName} onApplyFilters={onApplyFilters} defaultFilters={defaultFilters} />
	),
	deliverySLAPerformance: ({ widgetName, onApplyFilters, defaultFilters }) => (
		<DeliverySLAPerformance
			widgetName={widgetName}
			onApplyFilters={onApplyFilters}
			defaultFilters={defaultFilters}
		/>
	),
	dcPerformance: ({ widgetName, onApplyFilters, defaultFilters }) => (
		<DCPerformanceList widgetName={widgetName} onApplyFilters={onApplyFilters} defaultFilters={defaultFilters} />
	),
	deliveryCompDistribution: ({ widgetName, onApplyFilters, defaultFilters }) => (
		<DeliveryCompDistribution
			widgetName={widgetName}
			onApplyFilters={onApplyFilters}
			defaultFilters={defaultFilters}
		/>
	),
	shippingAccountingSummary: ({ widgetName, onApplyFilters, defaultFilters }) => (
		<Summary widgetName={widgetName} onApplyFilters={onApplyFilters} defaultFilters={defaultFilters} />
	),
	shippingFees: ({ widgetName, onApplyFilters, defaultFilters }) => (
		<ShippingFees widgetName={widgetName} onApplyFilters={onApplyFilters} defaultFilters={defaultFilters} />
	),
	DCShippingFees: ({ widgetName, onApplyFilters, defaultFilters }) => (
		<DCShippingFees widgetName={widgetName} onApplyFilters={onApplyFilters} defaultFilters={defaultFilters} />
	),
	OTOFees: ({ widgetName, onApplyFilters, defaultFilters }) => (
		<OTOFees widgetName={widgetName} onApplyFilters={onApplyFilters} defaultFilters={defaultFilters} />
	),
	'analysis.shippingAccounting.yourShippingPartners.codCollected': ({
		widgetName,
		onApplyFilters,
		defaultFilters
	}) => <CODCollected widgetName={widgetName} onApplyFilters={onApplyFilters} defaultFilters={defaultFilters} />,
	orderValuesSummary: ({ widgetName, onApplyFilters, defaultFilters }) => (
		<SalesBodyProvider defaultFilters={defaultFilters} onApplyFilters={onApplyFilters}>
			<OrderValuesSummarySales widgetName={widgetName} />
		</SalesBodyProvider>
	),
	shipmentPaymentType: ({ widgetName, onApplyFilters, defaultFilters }) => (
		<SalesBodyProvider defaultFilters={defaultFilters} onApplyFilters={onApplyFilters}>
			<ShipmentPaymentType
				widgetName={widgetName}
				onApplyFilters={onApplyFilters}
				defaultFilters={defaultFilters}
			/>
		</SalesBodyProvider>
	),
	ordersByCity: ({ widgetName, onApplyFilters, defaultFilters }) => (
		<SalesBodyProvider defaultFilters={defaultFilters} onApplyFilters={onApplyFilters}>
			<OrdersByCity widgetName={widgetName} onApplyFilters={onApplyFilters} defaultFilters={defaultFilters} />
		</SalesBodyProvider>
	),
	'sales.numberOfOrders': ({ widgetName, onApplyFilters, defaultFilters }) => (
		<SalesBodyProvider defaultFilters={defaultFilters} onApplyFilters={onApplyFilters}>
			<NumberOfOrders widgetName={widgetName} onApplyFilters={onApplyFilters} defaultFilters={defaultFilters} />
		</SalesBodyProvider>
	),
	avgBasketSize: ({ widgetName, onApplyFilters, defaultFilters }) => (
		<SalesBodyProvider defaultFilters={defaultFilters} onApplyFilters={onApplyFilters}>
			<AvgBasketSize widgetName={widgetName} onApplyFilters={onApplyFilters} defaultFilters={defaultFilters} />
		</SalesBodyProvider>
	),
	'oto-flex.orderValuesSummary': ({ widgetName, onApplyFilters, defaultFilters }) => (
		<OrderValuesSummary widgetName={widgetName} onApplyFilters={onApplyFilters} defaultFilters={defaultFilters} />
	),
	'oto-flex.numberOfOrders': ({ widgetName, onApplyFilters, defaultFilters }) => (
		<NumberOfOrdersOtoFlex
			widgetName={widgetName}
			onApplyFilters={onApplyFilters}
			defaultFilters={defaultFilters}
		/>
	),
	driverPerformance: ({ widgetName, onApplyFilters, defaultFilters }) => (
		<DriverPerformance widgetName={widgetName} onApplyFilters={onApplyFilters} defaultFilters={defaultFilters} />
	)
};

const MonitorViewComponent = ({ pageTitle, initialPage }) => {
	const [isButtonsVisible, setButtonsVisible] = useState(true);
	const data = chartsConfig(monitorViewRaw);
	const { pathname } = useLocation();
	const [widgetsData, setWidgetsData] = useState([]);
	const navigate = useNavigate();
	const [swiperRef, setSwiperRef] = useState(null);
	const [slides, setSlides] = useState([]);
	const [lastRequestedAt, setLastRequestedAt] = useState({});
	const [activeSlideIndex, setActiveSlideIndex] = useState(0);
	const axios = useAxiosPrivate();
	const { TimeZone } = useLocale();
	const { t } = useTranslation();

	const widgetsByPage = useMemo(() => {
		return slides?.reduce((acc, page) => {
			acc[page] = widgetsData.filter(
				(widget) => Array.isArray(widget.pages) && widget.pages.includes(page) && widget.isVisible
			);
			return acc;
		}, {});
	}, [slides, widgetsData]);

	const fetchWidgetsData = useCallback(async () => {
		try {
			const response = await axios.post(MONITOR_VIEW.GET_WIDGETS, { view: pathname });
			setWidgetsData(response.data);
			const allPages = response.data.flatMap((widget) => widget.pages || []);
			const uniquePages = Array.from(new Set(allPages));
			setSlides(uniquePages);
			const now = dayjs().toISOString();
			setLastRequestedAt(now);
		} catch (error) {
			AppLogger.error('Failed to fetch widgets data', error);
		}
	}, [axios, pathname]);

	useEffect(() => {
		fetchWidgetsData();

		const handleUpdateWidgetStore = () => {
			fetchWidgetsData();
		};
		window.addEventListener('updateWidgetStore', handleUpdateWidgetStore);
		return () => {
			window.removeEventListener('updateWidgetStore', handleUpdateWidgetStore);
		};
	}, [fetchWidgetsData]);

	useEffect(() => {
		if (slides.length === 0 || slides.length > 1) return;
		const interval = setInterval(() => {
			fetchWidgetsData();
		}, 600000);
		return () => clearInterval(interval);
	}, [fetchWidgetsData, slides.length]);

	useEffect(() => {
		const handleKeyDown = (event) => {
			if (event.key === 'Escape') {
				setButtonsVisible(true);
			}
		};
		window.addEventListener('keydown', handleKeyDown);
		return () => {
			window.removeEventListener('keydown', handleKeyDown);
		};
	}, []);

	const handleHideButtons = useCallback(() => setButtonsVisible(false), []);
	const handleExitFullScreen = useCallback(() => navigate(initialPage), [navigate]);
	const handleManageWidgets = useCallback(() => {
		NiceModal.show(ManageWidgetsModal, {
			data,
			...(widgetsData.length > 0 && { monitorViewData: widgetsData }),
			pages: slides
		});
	}, [data, widgetsData, slides]);

	const handleApplyFilters = useCallback(
		async (widgetId, filters) => {
			setWidgetsData((prev) => {
				const updated = prev.map((widget) =>
					widget.id === widgetId && widget.isDuplicate ? { ...widget, appliedFilters: filters } : widget
				);
				const prevWidget = prev.find((widget) => widget.id === widgetId && widget.isDuplicate);
				const prevFilters = prevWidget?.appliedFilters;
				const filtersChanged = prevFilters && JSON.stringify(prevFilters) !== JSON.stringify(filters);

				if (filtersChanged) {
					axios
						.post(MONITOR_VIEW.UPDATE_VIEW, {
							widgets: updated,
							view: pathname
						})
						.catch((error) => {
							AppLogger.error('Failed to update widget filters', error);
						});
				}
				return updated;
			});
		},
		[pathname, axios, setWidgetsData]
	);

	const shouldRenderSwiper = useMemo(
		() => slides.length > 0 && Object.values(widgetsByPage).some((widgets) => widgets.length > 0),
		[slides, widgetsByPage]
	);

	const renderedWidgetsByPage = useMemo(() => {
		const result = {};
		slides.forEach((page) => {
			result[page] = (widgetsByPage[page] || []).map((widget) => {
				const mappingKey = widget.originalId || widget.id;
				const WidgetComponent = widgetComponentMapping[mappingKey];
				if (!WidgetComponent) {
					return <div key={widget.id}>{`Widget ${mappingKey} not found`}</div>;
				}
				return (
					<WidgetWrapper
						key={widget.id}
						widgetId={widget.id}
						originalId={widget.originalId}
						onApplyFilters={handleApplyFilters}>
						{WidgetComponent({
							widgetName: widget.name,
							onApplyFilters: (filters) => handleApplyFilters(widget.id, filters),
							...(widget.isDuplicate && widget.appliedFilters
								? { defaultFilters: widget.appliedFilters }
								: {})
						})}
					</WidgetWrapper>
				);
			});
		});
		return result;
	}, [slides, widgetsByPage, handleApplyFilters]);

	const swiper = useMemo(() => {
		if (!shouldRenderSwiper) return null;
		return (
			<Swiper
				key={lastRequestedAt || 'initial'}
				spaceBetween={24}
				slidesPerView={1}
				autoHeight={true}
				onSwiper={setSwiperRef}
				modules={[Autoplay]}
				autoplay={{
					delay: 20000,
					disableOnInteraction: false
				}}
				onSlideChange={(swiper) => setActiveSlideIndex(swiper.activeIndex)}>
				{slides
					.filter((page) => (widgetsByPage[page] || []).length > 0)
					.map((page, idx) => (
						<SwiperSlide key={page}>
							<WidgetPageContainer>
								{activeSlideIndex === idx ? renderedWidgetsByPage[page] : null}
							</WidgetPageContainer>
						</SwiperSlide>
					))}
			</Swiper>
		);
	}, [shouldRenderSwiper, slides, widgetsByPage, handleApplyFilters, lastRequestedAt, activeSlideIndex]);

	useEffect(() => {
		if (!swiperRef || !swiperRef.updateAutoHeight) return;
		const container = document.querySelector('.swiper');
		if (!container) return;

		const hasVerticalScroll = container.scrollHeight > container.clientHeight;
		let debounceTimeout = null;
		let scrollListener = null;

		if (!hasVerticalScroll) {
			debounceTimeout = setTimeout(() => {
				swiperRef.update();
				swiperRef.updateAutoHeight();
			}, 1000);
		} else {
			scrollListener = () => {
				if (debounceTimeout) clearTimeout(debounceTimeout);
				debounceTimeout = setTimeout(() => {
					swiperRef.update();
					swiperRef.updateAutoHeight();
				}, 300);
				container.removeEventListener('scroll', scrollListener, true);
			};
			container.addEventListener('scroll', scrollListener, true);
		}

		return () => {
			if (debounceTimeout) clearTimeout(debounceTimeout);
			if (scrollListener) container.removeEventListener('scroll', scrollListener, true);
		};
	}, [swiperRef, activeSlideIndex]);

	useEffect(() => {
		if (slides.length > 0) {
			setActiveSlideIndex(0);
		}
	}, [slides, lastRequestedAt]);

	return (
		<div>
			<PageHeader title={pageTitle}>
				{isButtonsVisible ? (
					<>
						<Button
							icon={faEyeSlash}
							label={'hideButtons'}
							variant="borderless"
							onClick={handleHideButtons}
						/>
						<Button
							icon={faExpand}
							label={'dashboard.exitFullScreen'}
							variant="blackOutline"
							onClick={handleExitFullScreen}
						/>
						<Button
							icon={faObjectsColumn}
							label={'manageWidgets.buttonLabel'}
							variant="blue"
							onClick={handleManageWidgets}
						/>
					</>
				) : (
					<S.ShowButtonCaption>
						<Typography variant="caption" color="var(--content-3)">
							pressEscToSeeButtons
						</Typography>
					</S.ShowButtonCaption>
				)}
			</PageHeader>
			<Typography variant="caption" className="mb-2" color="var(--content-3)">
				{t('lastUpdatedAt')}
				{lastRequestedAt ? dayjs(lastRequestedAt).tz(TimeZone).format('DD.MM HH:mm') : 'N/A'}
			</Typography>
			<S.SwiperContainer>
				{shouldRenderSwiper ? (
					swiper
				) : (
					<S.Container>
						<S.EmptyDataCard fullHeight fullWidth>
							<Image
								url={
									'https://storage.googleapis.com/tryoto-public/public/assets/icons/no-data-card.svg'
								}
								alt="emptyDataCards"
								height="12rem"
								width="12rem"
								className="mt-4"
							/>
							<Typography variant="p2" className="my-4 text-center" fontWeight="700" noWrap gutterBottom>
								noDataCardsTitle
							</Typography>
							<Typography variant="p3" className="text-center" color="var(--content-3)" noWrap>
								noRecordsFoundSubtitleManageWidgets
							</Typography>
							<Button
								className="my-4"
								icon={faObjectsColumn}
								label={'manageWidgets.buttonLabel'}
								variant="blue"
								onClick={handleManageWidgets}
							/>
						</S.EmptyDataCard>
					</S.Container>
				)}
			</S.SwiperContainer>
			{swiperRef && <SwiperFooter swiperRef={swiperRef} />}
		</div>
	);
};

export default MonitorViewComponent;
